import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useStore } from '../store/useStore';
import { UsageSummary, UsageAlert, TokenUsage } from '../types/usage';
import { db } from '../utils/firebase';
import { MODELS_DATABASE, calculatePromptCost, calculateCompletionCost } from '../config/modelsDatabase';

interface UsageRecord {
  userId: string;
  modelId: string;
  usage: TokenUsage;
  timestamp: string;
  cost: number;
}

// Helper functions for calculating rates with UTC discounts
function getDiscountedPromptRate(modelId: string): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;

  // Apply DeepSeek UTC discount if applicable
  if (model.provider === 'deepseek') {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentTimeInMinutes = utcHour * 60 + utcMinutes;
    
    // Check if current time is within DeepSeek's discount period (UTC 16:30-00:30)
    const discountStartMinutes = 16 * 60 + 30; // 16:30 UTC
    const discountEndMinutes = 24 * 60 + 30;   // 00:30 UTC (next day)
    
    const isDiscountTime = 
      (currentTimeInMinutes >= discountStartMinutes) || // After 16:30
      (currentTimeInMinutes <= 30); // Before 00:30
    
    if (isDiscountTime) {
      // Apply model-specific discount
      if (modelId === 'deepseek-chat') {
        // 50% discount for DeepSeek Chat
        return model.pricing.prompt_tokens_per_1k * 0.5;
      } else if (modelId === 'deepseek-reasoner') {
        // 75% discount for DeepSeek Reasoner
        return model.pricing.prompt_tokens_per_1k * 0.25;
      }
    }
  }
  
  return model.pricing.prompt_tokens_per_1k;
}

function getDiscountedCompletionRate(modelId: string): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  
  // Apply DeepSeek UTC discount if applicable
  if (model.provider === 'deepseek') {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentTimeInMinutes = utcHour * 60 + utcMinutes;
    
    // Check if current time is within DeepSeek's discount period (UTC 16:30-00:30)
    const discountStartMinutes = 16 * 60 + 30; // 16:30 UTC
    const discountEndMinutes = 24 * 60 + 30;   // 00:30 UTC (next day)
    
    const isDiscountTime = 
      (currentTimeInMinutes >= discountStartMinutes) || // After 16:30
      (currentTimeInMinutes <= 30); // Before 00:30
    
    if (isDiscountTime) {
      // Apply model-specific discount
      if (modelId === 'deepseek-chat') {
        // 50% discount for DeepSeek Chat
        return model.pricing.completion_tokens_per_1k * 0.5;
      } else if (modelId === 'deepseek-reasoner') {
        // 75% discount for DeepSeek Reasoner
        return model.pricing.completion_tokens_per_1k * 0.25;
      }
    }
  }
  
  return model.pricing.completion_tokens_per_1k;
}

export class UsageService {
  private static instance: UsageService;

  private constructor() {}

  public static getInstance(): UsageService {
    if (!UsageService.instance) {
      UsageService.instance = new UsageService();
    }
    return UsageService.instance;
  }

  public async recordUsage(userId: string, modelId: string, usage: TokenUsage, topUpValue: number): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      const currentMonth = new Date().toISOString().substring(0, 7);
      
      // Extract token counts
      const promptTokens = usage.prompt_tokens || 0;
      const completionTokens = usage.completion_tokens || 0;
      const totalTokens = promptTokens + completionTokens;

      // Get model pricing from database
      const model = MODELS_DATABASE[modelId];
      if (!model) {
        throw new Error(`No pricing found for model: ${modelId}`);
      }

      // Calculate costs using the helper functions which handle UTC discounts
      const promptCost = (promptTokens / 1000) * getDiscountedPromptRate(modelId);
      const completionCost = (completionTokens / 1000) * getDiscountedCompletionRate(modelId);
      const cost = promptCost + completionCost;

      // Calculate new balance
      const newBalance = Math.max(0, topUpValue - cost);

      // Create usage record
      const usageRecord: UsageRecord = {
        userId,
        modelId,
        usage,
        timestamp: new Date().toISOString(),
        cost
      };

      // Update monthly summary
      const currentMonthSummary = userData.usageSummary?.[currentMonth] || {
        total_tokens: 0,
        total_cost: 0,
        breakdown_by_model: {}
      };

      const modelBreakdown = currentMonthSummary.breakdown_by_model[modelId] || {
        tokens: {
          prompt_tokens: 0,
          completion_tokens: 0,
          total_tokens: 0
        },
        cost: {
          prompt_cost: 0,
          completion_cost: 0,
          total_cost: 0,
          currency: model.pricing.currency
        }
      };

      // Update model breakdown
      modelBreakdown.tokens.prompt_tokens += promptTokens;
      modelBreakdown.tokens.completion_tokens += completionTokens;
      modelBreakdown.tokens.total_tokens += totalTokens;
      modelBreakdown.cost.prompt_cost += promptCost;
      modelBreakdown.cost.completion_cost += completionCost;
      modelBreakdown.cost.total_cost += cost;
      modelBreakdown.cost.currency = model.pricing.currency;

      // Update monthly totals
      currentMonthSummary.total_tokens += totalTokens;
      currentMonthSummary.total_cost += cost;
      currentMonthSummary.breakdown_by_model[modelId] = modelBreakdown;

      // Update Firebase with both usage data and new balance
      await updateDoc(userRef, {
        [`usageSummary.${currentMonth}`]: currentMonthSummary,
        topUpValue: newBalance,
        lastUpdated: new Date().toISOString()
      });

      // Update local state with new balance
      useStore.setState({ topUpValue: newBalance });

      // Force a re-render
      setTimeout(() => useStore.setState({}), 0);

      console.log('Usage recorded and balance updated:', {
        userId,
        modelId,
        totalTokens,
        cost,
        previousBalance: topUpValue,
        newBalance,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error recording usage:', error);
      throw error;
    }
  }

  public async updateBalance(userId: string, newBalance: number): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      
      // Update Firebase first
      await updateDoc(userRef, {
        topUpValue: newBalance,
        lastUpdated: new Date().toISOString()
      });

      // Then update local state
      useStore.setState({ topUpValue: newBalance });

      // Force a re-render
      setTimeout(() => useStore.setState({}), 0);

      console.log('Balance updated:', {
        userId,
        newBalance,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  }

  public async initialize(userId: string, topUpValue: number): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      console.log('Usage service initialized for user:', userId);
    } catch (error) {
      console.error('Error initializing usage service:', error);
      throw error;
    }
  }

  public async refreshUsageData(userId: string, topUpValue: number): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      // For now, just verify the document exists
      console.log('Usage data refreshed for user:', userId);
    } catch (error) {
      console.error('Error refreshing usage data:', error);
      throw error;
    }
  }

  public async getUsageSummary(userId: string, topUpValue: number, language: string = 'en'): Promise<UsageSummary> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      const currentMonth = new Date().toISOString().substring(0, 7);

      // Calculate average token rate among all models
      let totalRate = 0;
      let modelCount = 0;
      Object.values(MODELS_DATABASE).forEach(model => {
        const rate = model.pricing.prompt_tokens_per_1k + model.pricing.completion_tokens_per_1k;
        if (rate > 0) {
          totalRate += rate;
          modelCount++;
        }
      });
      const averageRate = totalRate / modelCount;

      // Calculate remaining tokens based on remaining balance and average rate
      // Multiply by 1000 since rates are per 1K tokens
      const estimatedRemainingTokens = Math.floor((topUpValue / averageRate) * 1000);

      return {
        current_month: userData.usageSummary?.[currentMonth] || {
          total_tokens: 0,
          total_cost: 0,
          breakdown_by_model: {}
        },
        limits: {
          token_limit_remaining: estimatedRemainingTokens,
          cost_limit_remaining: topUpValue,
          token_limit_note: language === 'es'
            ? '(basado en el precio promedio por token)'
            : '(based on average token price)'
        },
        historical: {
          monthly_totals: []
        }
      };
    } catch (error) {
      console.error('Error getting usage summary:', error);
      throw error;
    }
  }

  public async checkAlerts(userId: string, language: string): Promise<UsageAlert[]> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      const alerts: UsageAlert[] = [];

      // Check token limit
      const tokenLimit = userData.usagePreferences?.monthly_limits?.token_limit || 100000;
      const currentTokens = userData.usageSummary?.total_tokens || 0;
      const tokenPercentage = (currentTokens / tokenLimit) * 100;

      if (tokenPercentage >= 80) {
        alerts.push({
          type: 'token_limit',
          threshold: 80,
          message: language === 'es' 
            ? 'Has alcanzado el 80% de tu límite de tokens'
            : 'You have reached 80% of your token limit',
          current_value: currentTokens,
          limit: tokenLimit
        });
      }

      // Check cost limit
      const costLimit = userData.topUpValue || 0;
      const currentCost = userData.usageSummary?.total_cost || 0;
      const costPercentage = (currentCost / costLimit) * 100;

      if (costPercentage >= 80) {
        alerts.push({
          type: 'cost_limit',
          threshold: 80,
          message: language === 'es'
            ? 'Has alcanzado el 80% de tu límite de costos'
            : 'You have reached 80% of your cost limit',
          current_value: currentCost,
          limit: costLimit
        });
      }

      return alerts;
    } catch (error) {
      console.error('Error checking alerts:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const usageService = UsageService.getInstance();